import { storageService } from '../firebaseInstance';
import { uploadAndMakePublicAvatar, getPublicAvatarUrl } from './publicAvatarUtils';

/**
 * ⚠️ LEGACY COMPATIBILITY MODULE ⚠️
 * 
 * 이 모듈은 기존 코드와의 호환성을 위해 유지되지만,
 * 모든 함수는 새로운 Public URL 시스템으로 대체되었습니다.
 * 
 * 새로운 시스템:
 * - Firebase Auth 사용 안 함 (Threads userID만 사용)
 * - 복잡한 캐싱 로직 제거
 * - 빠른 Public URL 접근
 * - 예측 가능한 URL 패턴: https://storage.googleapis.com/bucket/avatars/{userID}.jpg
 */

/**
 * @deprecated 새로운 uploadAndMakePublicAvatar 함수 사용을 권장
 * 사용자 아바타 이미지를 Public URL로 업로드
 */
export const cacheUserAvatar = async (userID, threadsProfileUrl, forceUpdate = false) => {
  console.warn('⚠️ cacheUserAvatar is deprecated. Use uploadAndMakePublicAvatar instead.');
  
  try {
    return await uploadAndMakePublicAvatar(userID, threadsProfileUrl);
  } catch (error) {
    console.error('Legacy avatar caching failed:', error);
    // 실패 시 원본 URL 반환 (호환성 유지)
    return threadsProfileUrl;
  }
};

/**
 * @deprecated 새로운 getPublicAvatarUrl 함수 사용을 권장
 * 사용자 아바타 URL 가져오기
 */
export const getUserAvatar = async (userID, fallbackURL = null) => {
  console.warn('⚠️ getUserAvatar is deprecated. Use getPublicAvatarUrl instead.');
  
  if (!userID) {
    return fallbackURL || '/placeholder-avatar.png';
  }
  
  // Public URL 방식으로 즉시 반환
  return getPublicAvatarUrl(userID);
};

/**
 * @deprecated 새로운 Public URL 시스템에서는 실시간 구독 불필요
 * 실시간 아바타 URL 구독 (호환성 유지)
 */
export const subscribeToUserAvatar = (userID, callback) => {
  console.warn('⚠️ subscribeToUserAvatar is deprecated. Public URLs are static and don\'t need subscriptions.');
  
  if (!userID || typeof callback !== 'function') {
    return () => {};
  }
  
  // Public URL 즉시 반환
  const publicUrl = getPublicAvatarUrl(userID);
  callback(publicUrl);
  
  // 빈 구독 해제 함수 반환 (호환성 유지)
  return () => {};
};

/**
 * @deprecated 새로운 uploadAndMakePublicAvatar 함수 사용을 권장
 * 아바타 캐시 강제 갱신
 */
export const forceUpdateAvatar = async (userID, newThreadsURL) => {
  console.warn('⚠️ forceUpdateAvatar is deprecated. Use uploadAndMakePublicAvatar instead.');
  
  try {
    return await uploadAndMakePublicAvatar(userID, newThreadsURL);
  } catch (error) {
    console.error('Legacy force update failed:', error);
    throw error;
  }
};

/**
 * @deprecated Public URL 시스템에서는 수동 동기화 필요
 * 수동 아바타 싱크
 */
export const syncUserAvatar = async (userID, currentToken) => {
  console.warn('⚠️ syncUserAvatar is deprecated. Use uploadAndMakePublicAvatar with latest profile URL.');
  
  try {
    // 1. 최신 프로필 정보 가져오기
    const response = await fetch(
      `https://graph.threads.net/v1.0/me?fields=threads_profile_picture_url&access_token=${currentToken}`
    );
    
    if (!response.ok) {
      throw new Error(`Threads API error: ${response.status}`);
    }
    
    const profileData = await response.json();
    const latestImageURL = profileData.threads_profile_picture_url;
    
    if (!latestImageURL) {
      throw new Error('No profile image URL in API response');
    }
    
    // 2. 새로운 Public URL 시스템으로 업로드
    const newAvatarURL = await uploadAndMakePublicAvatar(userID, latestImageURL);
    
    return {
      success: true,
      updated: true,
      message: '프로필 이미지가 업데이트되었습니다.',
      url: newAvatarURL
    };
    
  } catch (error) {
    console.error('Legacy sync failed:', error);
    return {
      success: false,
      updated: false,
      message: `업데이트 실패: ${error.message}`,
      url: null
    };
  }
};

/**
 * @deprecated Public URL 시스템에서는 정리 불필요
 * 오래된 아바타 파일 정리 (호환성 유지)
 */
export const cleanupOldAvatar = async (userID) => {
  console.warn('⚠️ cleanupOldAvatar is deprecated. Public URL system handles overwrites automatically.');
  // 아무 작업 안 함 (Public URL 시스템에서는 자동으로 덮어쓰기)
};

/**
 * @deprecated Public URL 시스템에서는 상태 확인 간소화
 * 아바타 캐시 상태 확인 (호환성 유지)
 */
export const getAvatarCacheStatus = async (userID) => {
  console.warn('⚠️ getAvatarCacheStatus is deprecated. Public URLs are always available.');
  
  if (!userID) {
    return { status: 'invalid_user', cached: false };
  }
  
  // Public URL 시스템에서는 항상 사용 가능
  return {
    status: 'success',
    cached: true,
    recent: true,
    url: getPublicAvatarUrl(userID),
    lastUpdate: new Date(),
    needsUpdate: false
  };
};

/**
 * @deprecated Public URL 시스템에서는 배치 업데이트 불필요
 * 배치 아바타 업데이트 (호환성 유지)
 */
export const batchUpdateAvatars = async (users, onProgress = null) => {
  console.warn('⚠️ batchUpdateAvatars is deprecated. Use uploadAndMakePublicAvatar for individual updates.');
  
  const results = {
    total: users.length,
    success: 0,
    failed: 0,
    skipped: users.length, // 모든 사용자 스킵 (필요 시 개별 업데이트)
    errors: []
  };
  
  console.log('Batch update skipped - use individual uploadAndMakePublicAvatar calls instead');
  return results;
};

/**
 * @deprecated 새로운 checkAvatarExists 함수 사용을 권장
 * 이미지 URL 유효성 검사 (호환성 유지)
 */
export const validateImageUrl = async (imageUrl) => {
  console.warn('⚠️ validateImageUrl is deprecated. Use checkAvatarExists instead.');
  
  try {
    if (!imageUrl || typeof imageUrl !== 'string') {
      return false;
    }
    
    const response = await fetch(imageUrl, { method: 'HEAD' });
    return response.ok && response.headers.get('content-type')?.startsWith('image/');
    
  } catch (error) {
    console.warn('Image URL validation failed:', error.message);
    return false;
  }
};

/**
 * @deprecated Public URL 시스템에서는 Storage 파일 확인 불필요
 * Storage 파일 존재 여부 확인 (호환성 유지)
 */
const checkStorageFileExists = async (fileName) => {
  console.warn('⚠️ checkStorageFileExists is deprecated. Public URLs are always accessible.');
  
  try {
    const ref = storageService.ref(`avatars/${fileName}`);
    await ref.getDownloadURL();
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * @deprecated Public URL 시스템에서는 복잡한 캐싱 로직 불필요
 * 실제 아바타 캐싱 작업 (호환성 유지)
 */
const performAvatarCache = async (userID, threadsProfileUrl, forceUpdate) => {
  console.warn('⚠️ performAvatarCache is deprecated. Use uploadAndMakePublicAvatar instead.');
  
  try {
    return await uploadAndMakePublicAvatar(userID, threadsProfileUrl);
  } catch (error) {
    console.error('Legacy perform cache failed:', error);
    // 실패 시 원본 URL 반환 (호환성 유지)
    return threadsProfileUrl;
  }
};

// 사용자가 이 모듈을 import할 때 경고 메시지 출력
console.warn(`
🚨 AVATAR SYSTEM MIGRATION NOTICE 🚨

기존 avatarManager.js는 더 이상 사용되지 않습니다.
새로운 Public URL 시스템을 사용해주세요:

기존: import { cacheUserAvatar, getUserAvatar } from './avatarManager'
새로운: import { uploadAndMakePublicAvatar, getPublicAvatarUrl } from './publicAvatarUtils'

주요 변경사항:
✅ Firebase Auth 의존성 제거 (Threads userID만 사용)
✅ 복잡한 캐싱 로직 제거
✅ 빠른 Public URL 접근
✅ 예측 가능한 URL 패턴

자세한 내용은 publicAvatarUtils.js를 참고하세요.
`);